import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Pressable } from '../Common/Pressable'
import type { AmbientOpportunity } from '../../services/ai/ambient/ambientTypes'
import { useNls } from '../../nls/nlsProvider'

/**
 * The composer-slot chip for a single active ambient opportunity. Same
 * component on desktop and mobile; layout responsive to container width via
 * CSS. Consumers decide whether to render (off-topic priority, suppression
 * rules) — this component just renders what it's given.
 */
export const AmbientSuggestionChip = ({
  opportunity,
  onAccept,
  onModify,
  onDismiss,
}: {
  opportunity: AmbientOpportunity
  onAccept: (opportunity: AmbientOpportunity) => void
  onModify?: (opportunity: AmbientOpportunity) => void
  onDismiss: (opportunity: AmbientOpportunity) => void
}) => {
  const { t } = useNls()
  return (
    <div
      className="lineAmbientChip"
      role="status"
      aria-label={t('NLSAiAmbientSuggestionAria', { title: opportunity.title })}
    >
      <div className="lineAmbientChip__body">
        <span className="lineAmbientChip__glyph" aria-hidden="true"><AutoAwesomeRoundedIcon fontSize="inherit" /></span>
        <div className="lineAmbientChip__text">
          <strong className="lineAmbientChip__title">{opportunity.title}</strong>
          <span className="lineAmbientChip__reason">{opportunity.reason}</span>
        </div>
      </div>
      <div className="lineAmbientChip__actions">
        <Pressable
          type="button"
          className="lineAmbientChip__primary"
          onClick={() => onAccept(opportunity)}
        >
          {opportunity.primaryAction.label}
        </Pressable>
        {opportunity.secondaryAction && onModify && (
          <Pressable
            type="button"
            className="lineAmbientChip__secondary"
            onClick={() => onModify(opportunity)}
          >
            {opportunity.secondaryAction.label}
          </Pressable>
        )}
        <Pressable
          type="button"
          className="lineAmbientChip__dismiss"
          aria-label={t('NLSAiActionDismissSuggestion')}
          onClick={() => onDismiss(opportunity)}
        >
          <CloseRoundedIcon fontSize="inherit" />
        </Pressable>
      </div>
    </div>
  )
}
